import React, { useEffect, useState } from "react";
import { Field, getIn, useField, useFormikContext } from "formik";
import Tooltip from "components/Tooltip";
import { Icon } from "assets/Icon";

interface QuestionTypeTableRowResponseProps {
	defaultColumns?: number;
	disabled?: boolean;
	required?: boolean;
	fieldName: (name: string) => string;
}

export interface TableTypeColumnData {
	id: string;
	header: string;
	type: string;
}

export const QuestionTypeTableRowResponse: React.FC<
	QuestionTypeTableRowResponseProps
> = ({ disabled = false, defaultColumns = 2, required = false, fieldName }) => {
	const { setFieldValue, validateField, setFieldTouched } =
		useFormikContext<any>();
	const [field, meta] = useField<TableTypeColumnData[]>(fieldName("fields"));
	const [editCell, setEditCell] = useState<number | null>(null);

	const getFieldName = (index: number) =>
		`${fieldName("fields")}[${index}].header`;

	const createColumnData = (columnId: string, header: string) => ({
		id: columnId,
		header,
		type: "text",
	});

	// Initialize default columns if empty
	useEffect(() => {
		if (!field.value || field.value.length === 0) {
			const initialColumns: TableTypeColumnData[] = Array.from(
				{ length: defaultColumns },
				(_, index) => createColumnData(`col-${Date.now()}-${index}`, "")
			);
			setFieldValue(fieldName("fields"), initialColumns);
		}
	}, [field.value, setFieldValue, defaultColumns, fieldName]);

	const addColumn = () => {
		const newIndex = field.value.length;
		const newColumn = createColumnData(`col-${Date.now()}`, "");

		const newColumns = [...field.value, newColumn];
		setFieldValue(fieldName("fields"), newColumns);

		setTimeout(() => {
			const fieldPath = getFieldName(newIndex);
			setFieldTouched(fieldPath, true, true);
			validateField(fieldPath);
			setEditCell(newIndex); // open editor if desired
		}, 0);
	};

	const removeColumn = (columnId: string) => {
		if (field.value.length <= 1) {
			return;
		}
		setFieldValue(
			fieldName("fields"),
			field.value.filter((col) => col.id !== columnId)
		);
	};

	const handleCellClick = (index: number) => {
		if (!disabled) {
			setEditCell(index);
		}
	};

	const handleSaveCell = (index: number) => {
		setEditCell(null); // close edit
		const fieldPath = getFieldName(index);

		// mark as touched
		setFieldTouched(fieldPath, true, true);

		// validate this field explicitly
		validateField(fieldPath);
	};

	const isErroring = (index: number) => {
		const error = getIn(meta.error, `[${index}].header`);
		return meta.touched && error ? error : undefined;
	};

	return (
		<div className="w-full">
			<p>
				Please create the Column headings for your table below.
				<br />
				Respondents will add rows and enter data under each of these headings.
			</p>

			<div className="mt-4 overflow-x-auto">
				<table className="w-full table-auto rounded-xl border border-solid border-[#EAECF0] bg-white">
					<tbody>
						<tr className="border-b border-[#EAECF0]">
							{field.value?.map((column, index) => (
								<td
									key={column.id}
									className="relative border-r border-[#EAECF0] px-3 py-4 text-left align-top font-roboto font-normal text-primary-500"
									style={{ minWidth: "150px" }}
								>
									{editCell === index ? (
										<div className="flex flex-col gap-2">
											<Field
												name={getFieldName(index)}
												as="textarea"
												className="min-h-[100px] w-full resize-y rounded border border-gray-300 px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
												autoFocus
												placeholder="Enter text here..."
												onBlur={() => handleSaveCell(index)}
											/>
										</div>
									) : (
										<div className="group relative">
											<div
												onClick={() => handleCellClick(index)}
												className={`min-h-[100px] cursor-pointer whitespace-pre-wrap rounded ${isErroring(index) && "border border-red-500"} p-2 hover:bg-gray-50`}
											>
												{column.header ? (
													<span className="font-bold">{column.header}</span>
												) : (
													<span className="text-gray-400">Click to edit</span>
												)}
											</div>

											{field.value.length > 1 && !disabled && (
												<button
													type="button"
													onClick={() => removeColumn(column.id)}
													className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-red-600 opacity-0 shadow-sm transition-opacity hover:text-red-800 group-hover:opacity-100"
													title="Remove column"
												>
													<Tooltip label="Delete column (this cannot be undone)">
														<Icon
															style={{ width: "20px", height: "20px" }}
															icon="trash"
														/>
													</Tooltip>
												</button>
											)}
										</div>
									)}

									{/* Display validation error for this header */}
									<Field name={getFieldName(index)}>
										{({ meta }: any) =>
											meta.touched && meta.error ? (
												<p className="mt-1 text-sm text-red-500">
													{meta.error}
												</p>
											) : null
										}
									</Field>
								</td>
							))}

							<td className="w-20 px-3 py-4 text-center align-middle">
								<button
									type="button"
									onClick={addColumn}
									disabled={disabled}
									className="flex h-10 w-10 items-center justify-center rounded text-2xl text-blue-600 transition-colors hover:bg-blue-50 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
									title="Add column"
								>
									<Tooltip label="Add column">+</Tooltip>
								</button>
							</td>
						</tr>
					</tbody>
				</table>

				{/* Array-level validation (e.g., at least 1 column) */}
				{required && meta?.error && typeof meta.error === "string" && (
					<p className="mt-1 text-sm text-red-500">{meta.error}</p>
				)}
			</div>
		</div>
	);
};
